import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import DateInput from "../../common/components/form/DateInput.js";
import Select from "../../common/components/form/Select.js";
import { get } from "../../common/utils/api";
import { downloadFile, formatDate } from "../../common/utils/helpers";

export function jsonToCsv(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const text = value == null ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };
  return [headers, ...rows.map((row) => headers.map((key) => row[key]))]
    .map((row) => row.map(escape).join(","))
    .join("\r\n");
}

function ReviewForm({ groupOptions, t }) {
  const { initialValues, submitForm } = useFormikContext();
  // Automatically refresh form if input values change
  useEffect(() => {
    if (initialValues) {
      submitForm();
    }
  }, [initialValues, submitForm]);
  return (
    <React.Fragment>
      <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
        <div className="space-y-6 sm:space-y-5">
          <Select label={"Group"} name={"formGroup"} options={groupOptions} />
          <DateInput label={"Start date"} name={"formStartDate"} />
          <DateInput label={"End date"} name={"formEndDate"} />
        </div>
      </div>
      <div className="py-5">
        <div className="flex justify-end">
          <button
            type="submit"
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.generate", "Generate Report")}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

function Landcover4ReviewSummary({ showAlert, t }) {
  const formRef = useRef();
  const [groupOptions, setGroupOptions] = useState(["All"]);
  const [groupError, setGroupError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let active = true;
    get("/landcover4/candidate/groups/")
      .then((response) => {
        if (active) {
          setGroupOptions([
            "All",
            ...response.data.filter((group) => group !== "All"),
          ]);
        }
      })
      .catch(() => {
        if (active) {
          setGroupError(
            "Unable to load groups. Please reload the page to try again.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Default date range: past three days for now
  let today = new Date();
  let daysBefore = new Date();
  daysBefore.setDate(today.getDate() - 3);

  // Use session storage for the existing user and dates if they exist
  const initialValues = {
    formGroup: "All",
    formStartDate:
      sessionStorage.getItem("startDate") || formatDate(daysBefore),
    formEndDate: sessionStorage.getItem("endDate") || formatDate(today),
  };

  const exportTaskCSV = async () => {
    setDownloadError(null);
    const { formStartDate, formEndDate, formGroup } = formRef.current.values;
    const startTime = new Date(formStartDate + " 00:00:00.0").toISOString();
    const endTime = new Date(formEndDate + " 23:59:59.999").toISOString();
    try {
      const response = await get(
        `/landcover4/review/?username=All&start_date=${startTime}&end_date=${endTime}&group=${encodeURIComponent(
          formGroup,
        )}`,
      );
      downloadFile({
        data: jsonToCsv(response.data),
        fileName: "tasks.csv",
        fileType: "text/csv",
      });
    } catch {
      setDownloadError("Unable to download task data. Please try again.");
    }
  };

  const exportToGeoJson = (e) => {
    e.preventDefault();
    const start_date = formRef.current.values.formStartDate;
    const end_date = formRef.current.values.formEndDate;
    const group = formRef.current.values.formGroup;

    // Format the start_date & end_date to 00:00 & 23:59 in local time
    const start_time = new Date(start_date + " 00:00:00.0").toISOString();
    const end_time = new Date(end_date + " 23:59:59.999").toISOString();
    get(
      `/landcover4/download/?start_date=${start_time}&end_date=${end_time}&group=${encodeURIComponent(
        group,
      )}`,
    ).then((response) => {
      downloadFile({
        data: JSON.stringify(response.data),
        fileName: "download.geojson",
        fileType: "application/geo+json",
      });
    });
  };

  return (
    <React.Fragment>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 bg-white border-gray-200 shadow rounded-md pt-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.summary", "Generate Review Summary")}
            </h3>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={() => {}}
            innerRef={formRef}
          >
            {({ isSubmitting }) => (
              <React.Fragment>
                <Form className="space-y-8 divide-y divide-gray-200">
                  <ReviewForm groupOptions={groupOptions} t={t} />
                  {groupError && (
                    <p role="alert" className="text-sm text-red-800">
                      {groupError}
                    </p>
                  )}
                </Form>
              </React.Fragment>
            )}
          </Formik>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Review Summary")}
            </h3>
          </div>
        </div>
        {downloadError && (
          <p role="alert" className="text-sm text-red-800">
            {downloadError}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={exportTaskCSV}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.downloadTaskData", "Download task data")}
          </button>
          <button
            type="submit"
            onClick={exportToGeoJson}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.downloadData", "Download data as GeoJSON")}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}
export default withTranslation()(Landcover4ReviewSummary);
